import { useCallback, useEffect, useState } from 'react';

import {
  api,
  type AdminPeriodRow,
  type AdminUser,
  type IntegrationProviders,
  type IntegrationConfigEntry,
  type IntegrationTestResult,
  type OorwinSyncStatusResponse,
  type PeriodConfigResponse,
  type PeriodGapRow,
  type PersistedException,
  type SyncRunRow,
} from './api';
import { Badge, Card, CardHeader, Cell, Row, SearchBar, Spinner, Table } from './components/Ui';
import { Modal } from './components/Modal';
import { ResolveExceptionForm } from './components/ResolveExceptionForm';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { shortDate } from './format';
import { Shell, type ShellProps } from './Shell';

type Tab = 'users' | 'goals' | 'payroll' | 'periods' | 'integrations' | 'devtools';

type RoleGroup = 'all' | 'commission' | 'admin' | 'contractor' | 'read_only';

const ROLE_GROUPS: readonly { value: RoleGroup; label: string; roles: readonly string[] | null }[] =
  [
    { value: 'all', label: 'All Roles', roles: null },
    { value: 'commission', label: 'Commission Earners', roles: ['recruiter', 'salesperson'] },
    { value: 'admin', label: 'Admin', roles: ['owner', 'operations', 'finance'] },
    { value: 'contractor', label: 'Contractor', roles: ['contractor'] },
    { value: 'read_only', label: 'Read Only', roles: ['read_only'] },
  ];

const ROLES = [
  'owner',
  'operations',
  'finance',
  'recruiter',
  'salesperson',
  'read_only',
  'contractor',
] as const;

function roleBadgeTone(role: string): 'real' | 'warning' | 'neutral' {
  if (role === 'owner') return 'real';
  if (role === 'operations' || role === 'finance') return 'warning';
  return 'neutral';
}

function statusLabel(user: AdminUser): string {
  if (user.lockedUntil !== null && new Date(user.lockedUntil).getTime() > Date.now()) {
    return 'locked';
  }
  return user.isActive ? 'active' : 'inactive';
}

function statusBadgeTone(status: string): 'real' | 'warning' | 'neutral' {
  if (status === 'active') return 'real';
  if (status === 'locked') return 'warning';
  return 'neutral';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  return `${String(d.getDate())} ${MONTHS[d.getMonth()] ?? ''} ${String(d.getFullYear())}`;
}

/** A calendar date (YYYY-MM-DD), which `Date` would read as UTC midnight and shift. */
function formatDay(isoDay: string): string {
  const [year = '', month = '', day = ''] = isoDay.split('-');
  return `${String(Number(day))} ${MONTHS[Number(month) - 1] ?? ''} ${year}`;
}

function localToday(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${String(d.getFullYear())}-${month}-${day}`;
}

export function Admin(props: ShellProps) {
  const canManageUsers = props.session.permissions.includes('users:manage');
  const canWriteConfig = props.session.permissions.includes('config:write');
  const [tab, setTab] = useState<Tab>(canManageUsers ? 'users' : 'goals');

  const tabs: readonly { key: Tab; label: string; visible: boolean }[] = [
    { key: 'users', label: 'Users', visible: canManageUsers },
    { key: 'goals', label: 'Goals', visible: canWriteConfig },
    { key: 'payroll', label: 'Payroll Config', visible: canManageUsers },
    { key: 'periods', label: 'Pay Periods', visible: canManageUsers },
    { key: 'integrations', label: 'Integrations', visible: canManageUsers },
    { key: 'devtools', label: 'Developer Tools', visible: canManageUsers },
  ];

  const fillTab = tab === 'users' || tab === 'periods';

  return (
    <Shell {...props}>
      <div className="flex h-[calc(100vh-3rem)] flex-col">
        <div className="mb-4 flex shrink-0 items-center justify-between">
          <div className="flex w-fit gap-1 rounded-lg border border-edge bg-surface-base p-0.5">
            {tabs
              .filter((t) => t.visible)
              .map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                    tab === key
                      ? 'bg-surface-overlay text-ink'
                      : 'text-ink-faint hover:text-ink-muted'
                  }`}
                >
                  {label}
                </button>
              ))}
          </div>
        </div>

        <div className={`min-h-0 flex-1 ${fillTab ? 'overflow-hidden' : 'overflow-auto'}`}>
          {tab === 'users' && canManageUsers && <UsersSection />}
          {tab === 'goals' && canWriteConfig && <GoalsTab />}
          {tab === 'payroll' && canManageUsers && <PayrollConfigTab />}
          {tab === 'periods' && canManageUsers && <PeriodsTab />}
          {tab === 'integrations' && canManageUsers && <IntegrationsTab />}
          {tab === 'devtools' && canManageUsers && <DeveloperToolsTab />}
        </div>
      </div>
    </Shell>
  );
}

/* ── Users Tab ──────────────────────────────────────────────────────────── */

function UsersSection() {
  const [search, setSearch] = useState('');
  const [roleGroup, setRoleGroup] = useState<RoleGroup>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);

  const {
    data: users,
    error,
    loading,
    reload,
  } = useAsyncData(() => api.listUsers(), [], 'Could not load users.');

  if (error !== null) {
    return (
      <Card className="px-5 py-8">
        <p className="text-sm text-caution">{error}</p>
      </Card>
    );
  }

  if (loading || users === null) {
    return (
      <Card>
        <Spinner label="Loading users…" />
      </Card>
    );
  }

  const group = ROLE_GROUPS.find((g) => g.value === roleGroup);
  const filtered = users.filter((u) => {
    if (group?.roles !== null && group?.roles !== undefined && !group.roles.includes(u.role)) {
      return false;
    }
    if (search === '') return true;
    const q = search.toLowerCase();
    return (
      u.fullName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <Card className="flex h-full min-h-0 flex-col">
        <div className="shrink-0">
          <CardHeader
            title="Users"
            subtitle={`${String(filtered.length)} of ${String(users.length)}`}
            aside={
              <div className="flex items-center gap-3">
                <select
                  value={roleGroup}
                  onChange={(e) => setRoleGroup(e.target.value as RoleGroup)}
                  className="rounded-lg border border-edge bg-surface-base px-3 py-1.5 text-xs text-ink"
                  aria-label="Filter by role group"
                >
                  {ROLE_GROUPS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </select>
                <div className="w-56">
                  <SearchBar value={search} onChange={setSearch} placeholder="Filter users…" />
                </div>
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="rounded-lg border border-profit bg-profit/10 px-4 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors"
                >
                  Add User
                </button>
              </div>
            }
          />
        </div>
        <UserTable users={filtered} onSelect={setEditUser} />
      </Card>

      <AddUserModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={reload} />

      <EditUserModal user={editUser} onClose={() => setEditUser(null)} onUpdated={reload} />
    </>
  );
}

/* ── User Table ────────────────────────────────────────────────────────── */

function UserTable({
  users,
  onSelect,
}: {
  users: readonly AdminUser[];
  onSelect: (user: AdminUser) => void;
}) {
  const accessor = useCallback((row: AdminUser, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.fullName,
      email: row.email,
      role: row.role,
      status: statusLabel(row),
      lastLogin: row.lastLoginAt,
      created: row.createdAt,
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(users, accessor);

  return (
    <Table
      sticky
      head={[
        { label: 'Name', sortKey: 'name' },
        { label: 'Email', sortKey: 'email' },
        { label: 'Role', sortKey: 'role' },
        { label: 'Status', sortKey: 'status' },
        { label: 'Last Login', sortKey: 'lastLogin' },
        { label: 'Created', sortKey: 'created' },
      ]}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={onSort}
    >
      {sorted.map((user) => {
        const status = statusLabel(user);
        return (
          <Row key={user.id} onClick={() => onSelect(user)}>
            <Cell>
              <span className="text-ink cursor-pointer hover:text-profit transition-colors">
                {user.fullName}
              </span>
            </Cell>
            <Cell muted>{user.email}</Cell>
            <Cell>
              <Badge tone={roleBadgeTone(user.role)}>{user.role.replace('_', ' ')}</Badge>
            </Cell>
            <Cell>
              <Badge tone={statusBadgeTone(status)}>{status}</Badge>
            </Cell>
            <Cell muted>{formatDate(user.lastLoginAt)}</Cell>
            <Cell muted>{formatDate(user.createdAt)}</Cell>
          </Row>
        );
      })}
    </Table>
  );
}

/* ── Add User Modal ────────────────────────────────────────────────────── */

function AddUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<string>('read_only');
  const [password, setPassword] = useState('');
  const [workerId, setWorkerId] = useState<string>('');
  const [workerSearch, setWorkerSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const { data: workerList } = useAsyncData(
    () => (role === 'contractor' ? api.getWorkerList() : Promise.resolve({ workers: [] })),
    [role],
  );

  const reset = () => {
    setEmail('');
    setFullName('');
    setRole('read_only');
    setPassword('');
    setWorkerId('');
    setWorkerSearch('');
    setFieldError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);

    if (!email.trim() || !fullName.trim() || !password.trim()) {
      setFieldError('All fields are required.');
      return;
    }
    if (password.length < 10) {
      setFieldError('Password must be at least 10 characters.');
      return;
    }

    if (role === 'contractor' && !workerId) {
      setFieldError('Select a worker to link this contractor account to.');
      return;
    }

    setSubmitting(true);
    try {
      await api.createUser({
        email: email.trim(),
        fullName: fullName.trim(),
        role,
        password,
        ...(workerId ? { workerId } : {}),
      });
      reset();
      onClose();
      onCreated();
    } catch (err) {
      setFieldError(err instanceof Error ? err.message : 'Failed to create user.');
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Add User">
      <form onSubmit={handleSubmit} className="space-y-4">
        {fieldError !== null && <p className="text-sm text-caution">{fieldError}</p>}

        {/* Role first — drives the rest of the form */}
        <label className="block">
          <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">Role</span>
          <select
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              if (e.target.value !== 'contractor') {
                setWorkerId('');
                setWorkerSearch('');
              }
            }}
            className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r.replace('_', ' ')}
              </option>
            ))}
          </select>
        </label>

        {/* Worker picker — only for contractors, auto-fills name + email */}
        {role === 'contractor' && (
          <label className="block">
            <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
              Select Contractor
            </span>
            <input
              type="text"
              value={workerSearch}
              onChange={(e) => {
                setWorkerSearch(e.target.value);
                setWorkerId('');
              }}
              onFocus={() => {
                if (workerId) setWorkerSearch('');
              }}
              placeholder="Search by name or client…"
              className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
            />
            <div className="mt-1 max-h-64 overflow-auto rounded-lg border border-edge bg-surface-base">
              {(workerList?.workers ?? [])
                .filter(
                  (w) =>
                    !w.hasUserAccount &&
                    (workerSearch === '' ||
                      w.fullName.toLowerCase().includes(workerSearch.toLowerCase()) ||
                      w.clientName.toLowerCase().includes(workerSearch.toLowerCase())),
                )
                .map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => {
                      setWorkerId(w.id);
                      setFullName(w.fullName);
                      setEmail(w.email ?? '');
                      setWorkerSearch(w.fullName);
                    }}
                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-surface-overlay transition-colors ${
                      workerId === w.id ? 'bg-profit/10 text-profit font-medium' : 'text-ink'
                    }`}
                  >
                    <span>{w.fullName}</span>
                    <span className="ml-2 text-xs text-ink-faint">{w.clientName}</span>
                    {!w.hasLoginAccess && (
                      <span className="ml-1 text-[10px] text-caution">(no Oorwin login)</span>
                    )}
                  </button>
                ))}
              {(workerList?.workers ?? []).filter((w) => !w.hasUserAccount).length === 0 && (
                <p className="px-3 py-2 text-xs text-ink-faint">
                  All workers already have accounts
                </p>
              )}
            </div>
          </label>
        )}

        {/* Email — auto-filled for contractors, manual for others */}
        <label className="block">
          <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
            placeholder="user@example.com"
            required
          />
        </label>

        {/* Full Name — auto-filled for contractors */}
        <label className="block">
          <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
            Full Name
          </span>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
            placeholder="Jane Smith"
            required
          />
        </label>

        {/* Password — always manual */}
        <label className="block">
          <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
            placeholder="Minimum 10 characters"
            minLength={10}
            required
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-edge px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink hover:border-edge-strong transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg border border-profit bg-profit/10 px-4 py-2 text-sm font-medium text-profit hover:bg-profit/20 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create User'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Edit User Modal ───────────────────────────────────────────────────── */

function EditUserModal({
  user,
  onClose,
  onUpdated,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [inactiveDate, setInactiveDate] = useState<string>('');
  const [editWorkerId, setEditWorkerId] = useState<string>('');
  const [editWorkerSearch, setEditWorkerSearch] = useState('');
  const [roleEffectiveDate, setRoleEffectiveDate] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const isEarnerRole = role === 'recruiter' || role === 'salesperson';

  const { data: editWorkerList } = useAsyncData(
    () => (role === 'contractor' ? api.getWorkerList() : Promise.resolve({ workers: [] })),
    [role],
  );

  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [resetPwError, setResetPwError] = useState<string | null>(null);
  const [resetPwSubmitting, setResetPwSubmitting] = useState(false);

  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [revokeResult, setRevokeResult] = useState<string | null>(null);

  const open = user !== null;

  // Sync form state when user changes
  const [prevUserId, setPrevUserId] = useState<string | null>(null);
  if (user !== null && user.id !== prevUserId) {
    setPrevUserId(user.id);
    setFullName(user.fullName);
    setRole(user.role);
    setIsActive(user.isActive);
    setInactiveDate(user.inactiveDate ?? '');
    setEditWorkerId(user.workerId ?? '');
    setEditWorkerSearch(user.workerName ?? '');
    setRoleEffectiveDate(user.roleEffectiveDate ?? '');
    setFieldError(null);
    setSubmitting(false);
    setResetPwOpen(false);
    setResetPw('');
    setResetPwError(null);
    setRevokeResult(null);
  }
  if (user === null && prevUserId !== null) {
    setPrevUserId(null);
  }

  const handleSave = async () => {
    if (user === null) return;
    setFieldError(null);

    if (!fullName.trim()) {
      setFieldError('Name is required.');
      return;
    }

    setSubmitting(true);
    try {
      await api.updateUser(user.id, {
        fullName: fullName.trim(),
        role,
        // Earner roles use inactiveDate (which auto-syncs is_active on the server).
        // Non-earner roles use the boolean toggle directly.
        ...(role === 'recruiter' || role === 'salesperson'
          ? { inactiveDate: inactiveDate || null, roleEffectiveDate: roleEffectiveDate || null }
          : { isActive }),
        ...(role === 'contractor' ? { workerId: editWorkerId || null } : {}),
      });
      setSubmitting(false);
      onClose();
      onUpdated();
    } catch (err) {
      setFieldError(err instanceof Error ? err.message : 'Failed to update user.');
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (user === null) return;
    setResetPwError(null);

    if (resetPw.length < 10) {
      setResetPwError('Password must be at least 10 characters.');
      return;
    }

    setResetPwSubmitting(true);
    try {
      await api.resetUserPassword(user.id, resetPw);
      setResetPwOpen(false);
      setResetPw('');
      setResetPwSubmitting(false);
    } catch (err) {
      setResetPwError(err instanceof Error ? err.message : 'Failed to reset password.');
      setResetPwSubmitting(false);
    }
  };

  const handleRevokeSessions = async () => {
    if (user === null) return;
    setRevokeSubmitting(true);
    try {
      const result = await api.revokeUserSessions(user.id);
      setRevokeResult(`Revoked ${String(result.revoked)} session(s).`);
      setRevokeSubmitting(false);
    } catch (err) {
      setRevokeResult(err instanceof Error ? err.message : 'Failed to revoke sessions.');
      setRevokeSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit User">
      {user !== null && (
        <div className="space-y-4">
          {fieldError !== null && <p className="text-sm text-caution">{fieldError}</p>}

          {/* Role first */}
          <label className="block">
            <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">Role</span>
            <select
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                if (e.target.value !== 'contractor') {
                  setEditWorkerId('');
                  setEditWorkerSearch('');
                }
              }}
              className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>

          {(role === 'recruiter' || role === 'salesperson') && (
            <label className="block">
              <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
                Role Start Date
              </span>
              <input
                type="date"
                value={roleEffectiveDate}
                onChange={(e) => setRoleEffectiveDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
              />
              <p className="mt-1 text-[11px] text-ink-faint">
                When this person started their role. Contractors placed during the first 18 months
                get the elevated commission rate.
              </p>
            </label>
          )}

          {role === 'contractor' && (
            <label className="block">
              <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
                Linked Worker
              </span>
              <input
                type="text"
                value={editWorkerSearch}
                onChange={(e) => setEditWorkerSearch(e.target.value)}
                placeholder="Search workers…"
                className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
              />
              <div className="mt-1 max-h-40 overflow-auto rounded-lg border border-edge bg-surface-base">
                {(editWorkerList?.workers ?? [])
                  .filter(
                    (w) =>
                      editWorkerSearch === '' ||
                      w.fullName.toLowerCase().includes(editWorkerSearch.toLowerCase()) ||
                      w.clientName.toLowerCase().includes(editWorkerSearch.toLowerCase()),
                  )
                  .filter((w) => !w.hasUserAccount || w.id === editWorkerId)
                  .slice(0, 20)
                  .map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => {
                        setEditWorkerId(w.id);
                        setEditWorkerSearch(w.fullName);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-sm hover:bg-surface-overlay transition-colors ${
                        editWorkerId === w.id ? 'bg-profit/10 text-profit' : 'text-ink'
                      }`}
                    >
                      <span className="font-medium">{w.fullName}</span>
                      <span className="ml-2 text-xs text-ink-faint">{w.clientName}</span>
                    </button>
                  ))}
              </div>
            </label>
          )}

          {/* Email */}
          <label className="block">
            <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
              Email
            </span>
            <input
              type="email"
              value={user.email}
              disabled
              className="mt-1 w-full rounded-lg border border-edge bg-surface-overlay px-3 py-2 text-sm text-ink-muted cursor-not-allowed"
            />
          </label>

          {/* Full Name */}
          <label className="block">
            <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
              Full Name
            </span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
            />
          </label>

          {/* Active / Inactive Date — earner roles use a date, others use a toggle */}
          {isEarnerRole ? (
            <label className="block">
              <span className="text-xs font-medium text-ink-faint uppercase tracking-wide">
                Inactive Date
              </span>
              <input
                type="date"
                value={inactiveDate}
                onChange={(e) => setInactiveDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm text-ink focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
              />
              <p className="mt-1 text-xs text-ink-faint">
                {inactiveDate
                  ? 'This earner will be hidden from screens after this date but still appear in historical reports.'
                  : 'Leave blank while the earner is active.'}
              </p>
            </label>
          ) : (
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-edge text-profit focus:ring-profit/30"
              />
              <span className="text-sm text-ink">Active</span>
            </label>
          )}

          <div className="border-t border-edge pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setResetPwOpen(!resetPwOpen)}
                className="rounded-lg border border-edge px-3 py-1.5 text-xs font-medium text-ink-muted hover:text-ink hover:border-edge-strong transition-colors"
              >
                Reset Password
              </button>
              <button
                type="button"
                onClick={handleRevokeSessions}
                disabled={revokeSubmitting}
                className="rounded-lg border border-caution-dim px-3 py-1.5 text-xs font-medium text-caution hover:bg-caution/10 transition-colors disabled:opacity-50"
              >
                {revokeSubmitting ? 'Revoking…' : 'Revoke All Sessions'}
              </button>
            </div>

            {revokeResult !== null && <p className="text-xs text-ink-muted">{revokeResult}</p>}

            {resetPwOpen && (
              <div className="space-y-2 rounded-lg border border-edge bg-surface-base p-3">
                {resetPwError !== null && <p className="text-sm text-caution">{resetPwError}</p>}
                <input
                  type="password"
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  placeholder="New password (min 10 characters)"
                  className="w-full rounded-lg border border-edge bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-profit focus:outline-none focus:ring-1 focus:ring-profit/30"
                />
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resetPwSubmitting}
                  className="rounded-lg border border-profit bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors disabled:opacity-50"
                >
                  {resetPwSubmitting ? 'Resetting…' : 'Confirm Reset'}
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-edge px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink hover:border-edge-strong transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting}
              className="rounded-lg border border-profit bg-profit/10 px-4 py-2 text-sm font-medium text-profit hover:bg-profit/20 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Goals Tab ─────────────────────────────────────────────────────────── */

function GoalsTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [annual, setAnnual] = useState('');
  const [weeklyStart, setWeeklyStart] = useState('');
  const [increment, setIncrement] = useState('');
  const [holidayDefault, setHolidayDefault] = useState('');
  const [personId, setPersonId] = useState('');
  const [personAnnual, setPersonAnnual] = useState('');
  const [personStart, setPersonStart] = useState('');
  const [personIncrement, setPersonIncrement] = useState('75');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data, loading, reload } = useAsyncData(
    () => api.listGoalsAdmin(year),
    [year],
    'Could not load goal plans.',
  );

  useEffect(() => {
    if (data?.company === null || data?.company === undefined) {
      setAnnual('');
      setWeeklyStart('');
      setIncrement('');
      setHolidayDefault('');
      return;
    }
    setAnnual(data.company.annual_amount);
    setWeeklyStart(data.company.regular_weekly_start);
    setIncrement(data.company.weekly_increment);
    setHolidayDefault(data.company.holiday_default_amount ?? '');
  }, [data]);

  const saveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.saveGoalsAdmin({
        year,
        company: {
          annualAmount: annual,
          regularWeeklyStart: weeklyStart,
          weeklyIncrement: increment,
          ...(holidayDefault.trim() === '' ? {} : { holidayDefault }),
        },
      });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the company plan.');
    } finally {
      setSaving(false);
    }
  };

  const savePerson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (personId === '') return;
    setSaving(true);
    setError('');
    try {
      await api.saveGoalsAdmin({
        year,
        people: [
          {
            userId: personId,
            annualAmount: personAnnual,
            regularWeeklyStart: personStart,
            weeklyIncrement: personIncrement,
          },
        ],
      });
      setPersonId('');
      setPersonAnnual('');
      setPersonStart('');
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the personal plan.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || data === null) {
    return (
      <Card>
        <Spinner label="Loading goals…" />
      </Card>
    );
  }

  const plannedIds = new Set(data.people.map((p) => p.user_id));
  const available = data.recruiters.filter((r) => !plannedIds.has(r.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label htmlFor="goal-year" className="text-sm text-ink-muted">
          Year
        </label>
        <input
          id="goal-year"
          type="number"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="w-28 rounded-lg border border-edge bg-surface-base px-3 py-1.5 text-sm"
        />
      </div>

      <Card>
        <CardHeader
          title="Company plan"
          subtitle="One annual target. Weekly amounts are generated from the start and increment."
        />
        <form onSubmit={(e) => void saveCompany(e)} className="grid gap-3 px-5 py-4 md:grid-cols-2">
          <GoalField
            id="company-annual"
            label="Annual amount"
            value={annual}
            onChange={setAnnual}
          />
          <GoalField
            id="company-start"
            label="Regular week start"
            value={weeklyStart}
            onChange={setWeeklyStart}
          />
          <GoalField
            id="company-increment"
            label="Weekly increment"
            value={increment}
            onChange={setIncrement}
          />
          <GoalField
            id="company-holiday"
            label="Holiday default (optional)"
            value={holidayDefault}
            onChange={setHolidayDefault}
          />
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={saving || annual === '' || weeklyStart === '' || increment === ''}
              className="rounded-lg bg-profit px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save company plan'}
            </button>
          </div>
        </form>
      </Card>

      <Card className="flex max-h-[min(32rem,calc(100vh-16rem))] flex-col">
        <CardHeader
          title="Personal plans"
          subtitle={`${String(data.people.length)} people have a ${String(year)} goal`}
        />
        {data.people.length === 0 ? (
          <p className="px-5 py-3 text-sm text-ink-muted">No personal plans for this year yet.</p>
        ) : (
          <Table
            sticky
            head={[
              { label: 'Name' },
              { label: 'Role' },
              { label: 'Annual', align: 'right' },
              { label: 'Week start', align: 'right' },
              { label: 'Increment', align: 'right' },
            ]}
          >
            {data.people.map((p) => (
              <Row key={p.user_id}>
                <Cell>{p.user_name}</Cell>
                <Cell>
                  <span className="capitalize">{p.role}</span>
                </Cell>
                <Cell align="right" mono>
                  {p.annual_amount}
                </Cell>
                <Cell align="right" mono>
                  {p.regular_weekly_start}
                </Cell>
                <Cell align="right" mono>
                  {p.weekly_increment}
                </Cell>
              </Row>
            ))}
          </Table>
        )}
        <form
          onSubmit={(e) => void savePerson(e)}
          className="grid gap-3 border-t border-edge px-5 py-4 md:grid-cols-2"
        >
          <div>
            <label htmlFor="person-id" className="mb-1 block text-sm font-medium text-ink-muted">
              Person
            </label>
            <select
              id="person-id"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm"
            >
              <option value="">Select…</option>
              {available.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.full_name} ({r.role})
                </option>
              ))}
            </select>
          </div>
          <GoalField
            id="person-annual"
            label="Annual amount"
            value={personAnnual}
            onChange={setPersonAnnual}
          />
          <GoalField
            id="person-start"
            label="Regular week start"
            value={personStart}
            onChange={setPersonStart}
          />
          <GoalField
            id="person-increment"
            label="Weekly increment"
            value={personIncrement}
            onChange={setPersonIncrement}
          />
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={saving || personId === '' || personAnnual === '' || personStart === ''}
              className="rounded-lg bg-profit px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Add personal plan'}
            </button>
          </div>
        </form>
      </Card>
      {error !== '' && <p className="text-sm text-caution">{error}</p>}
    </div>
  );
}

function GoalField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink-muted">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm"
      />
    </div>
  );
}

/* ── Payroll Config Tab ────────────────────────────────────────────────── */

function PayrollConfigTab() {
  return (
    <div className="space-y-4">
      <BurdenConfigCard />
      <CommissionPlansCard />
      <CommissionAllocationsCard />
      <VerificationThresholdsCard />
    </div>
  );
}

/* ── Pay Periods Tab ───────────────────────────────────────────────────── */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function periodStatusTone(status: string): 'real' | 'warning' | 'neutral' {
  switch (status) {
    case 'closed':
      return 'real';
    case 'open':
    case 'reopened':
      return 'neutral';
    default:
      return 'warning';
  }
}

const PERIOD_TRANSITIONS: Record<string, string[]> = {
  open: ['in_review'],
  in_review: ['open', 'approved'],
  approved: ['in_review', 'submitted'],
  submitted: ['approved', 'closed'],
  closed: ['reopened'],
  reopened: ['open'],
};

function transitionLabel(status: string): string {
  switch (status) {
    case 'open':
      return 'Open';
    case 'in_review':
      return 'Review';
    case 'approved':
      return 'Approve';
    case 'submitted':
      return 'Submit';
    case 'closed':
      return 'Close';
    case 'reopened':
      return 'Reopen';
    default:
      return status;
  }
}

function PeriodsTab() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [generateCount, setGenerateCount] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [bulkClosing, setBulkClosing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: periodsData,
    loading: periodsLoading,
    reload: reloadPeriods,
  } = useAsyncData<{ periods: AdminPeriodRow[] }>(
    () => api.listPeriodsAdmin(),
    [],
    'Could not load pay periods.',
  );

  const { data: gapsData, reload: reloadGaps } = useAsyncData<{ gaps: PeriodGapRow[] }>(
    () => api.periodGaps(),
    [],
    'Could not load gap data.',
  );

  const { data: configData, reload: reloadConfig } = useAsyncData<PeriodConfigResponse>(
    () => api.periodConfig(),
    [],
    'Could not load period config.',
  );
  const [goLiveDraft, setGoLiveDraft] = useState<string | null>(null);
  const [savingGoLive, setSavingGoLive] = useState(false);

  const reload = useCallback(() => {
    reloadPeriods();
    reloadGaps();
    reloadConfig();
  }, [reloadPeriods, reloadGaps, reloadConfig]);

  const periods = periodsData?.periods ?? [];
  const gaps = gapsData?.gaps ?? [];

  const filtered =
    statusFilter === 'all' ? periods : periods.filter((p) => p.status === statusFilter);

  const openPeriods = periods.filter((p) => p.status !== 'closed' && p.status !== 'submitted');

  const handleTransition = async (periodId: string, newStatus: string) => {
    setActionError(null);
    try {
      await api.transitionPeriodStatus(periodId, newStatus);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Transition failed');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setActionError(null);
    try {
      await api.generatePeriods(generateCount);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkClose = async () => {
    if (selected.size === 0) return;
    setBulkClosing(true);
    setActionError(null);
    try {
      const result = await api.bulkClosePeriods([...selected]);
      setSelected(new Set());
      if (result.errors.length > 0) {
        setActionError(
          `Closed ${String(result.closed)}, skipped ${String(result.skipped)}: ${result.errors.slice(0, 3).join('; ')}`,
        );
      }
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Bulk close failed');
    } finally {
      setBulkClosing(false);
    }
  };

  const handleDelete = async (id: string) => {
    setActionError(null);
    try {
      await api.deletePeriod(id);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const saveGoLive = async (value: string | null) => {
    setSavingGoLive(true);
    setActionError(null);
    try {
      await api.updatePeriodConfig({ payrollGoLiveDate: value });
      setGoLiveDraft(null);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not save the go-live date');
    } finally {
      setSavingGoLive(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllOpen = () => {
    setSelected(new Set(openPeriods.map((p) => p.id)));
  };

  if (periodsLoading) {
    return (
      <Card>
        <Spinner label="Loading pay periods..." />
      </Card>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {/* Gap warnings */}
      {gaps.length > 0 && (
        <div className="shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <h3 className="text-sm font-semibold text-amber-800">
            {gaps.length} gap{gaps.length !== 1 ? 's' : ''} detected in period timeline
          </h3>
          <ul className="mt-1 text-sm text-amber-700 list-disc pl-5">
            {gaps.map((g) => (
              <li key={g.gapStart}>
                {g.gapStart} to {g.gapEnd} ({String(g.gapDays)} day{g.gapDays !== 1 ? 's' : ''})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Generate and config */}
      <Card className="shrink-0">
        <CardHeader title="Period Generation" />
        <div className="px-4 pb-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-ink-muted">Week ending day:</label>
              <span className="text-sm font-medium">
                {configData !== null
                  ? (DAY_NAMES[configData.payWeekEndingDay] ?? 'Unknown')
                  : '...'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-ink-muted">Generate</label>
              <input
                type="number"
                min={1}
                max={52}
                value={generateCount}
                onChange={(e) => setGenerateCount(Number(e.target.value))}
                className="w-16 rounded border border-edge bg-surface-base px-2 py-1 text-sm"
              />
              <span className="text-sm text-ink-muted">week(s)</span>
              <button
                type="button"
                disabled={generating}
                onClick={() => void handleGenerate()}
                className="rounded-md bg-profit px-3 py-1.5 text-sm font-medium text-white hover:bg-profit/90 disabled:opacity-50"
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="payroll-go-live" className="text-sm text-ink-muted">
                Payroll go-live:
              </label>
              {goLiveDraft === null ? (
                <>
                  <span className="text-sm font-medium">
                    {configData === null
                      ? '...'
                      : configData.payrollGoLiveDate === null
                        ? 'Not set'
                        : formatDay(configData.payrollGoLiveDate)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setGoLiveDraft(configData?.payrollGoLiveDate ?? localToday())}
                    className="text-xs text-ink-muted underline hover:text-ink"
                  >
                    {configData?.payrollGoLiveDate == null ? 'Set' : 'Change'}
                  </button>
                  {configData?.payrollGoLiveDate != null && (
                    <button
                      type="button"
                      disabled={savingGoLive}
                      onClick={() => void saveGoLive(null)}
                      className="text-xs text-ink-muted underline hover:text-ink disabled:opacity-50"
                    >
                      Clear
                    </button>
                  )}
                </>
              ) : (
                <>
                  <input
                    id="payroll-go-live"
                    type="date"
                    value={goLiveDraft}
                    onChange={(e) => setGoLiveDraft(e.target.value)}
                    className="rounded border border-edge bg-surface-base px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    disabled={savingGoLive || goLiveDraft === ''}
                    onClick={() => void saveGoLive(goLiveDraft)}
                    className="rounded-md bg-profit px-3 py-1.5 text-sm font-medium text-white hover:bg-profit/90 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setGoLiveDraft(null)}
                    className="text-xs text-ink-muted underline hover:text-ink"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
          <p className="mt-2 text-xs text-ink-faint">
            Weeks ending before the payroll go-live date were paid through the previous process and
            are never flagged overdue. Until it is set, nothing is.
          </p>
        </div>
      </Card>

      {/* Error banner */}
      {actionError !== null && (
        <div className="shrink-0 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      )}

      {/* Filters and bulk actions */}
      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 px-4 py-3 border-b border-edge flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-1 rounded-lg border border-edge bg-surface-base p-0.5">
            {(['all', 'open', 'in_review', 'approved', 'submitted', 'closed'] as const).map(
              (key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    statusFilter === key
                      ? 'bg-surface-overlay text-ink'
                      : 'text-ink-faint hover:text-ink-muted'
                  }`}
                >
                  {key === 'all'
                    ? 'All'
                    : key === 'in_review'
                      ? 'In Review'
                      : key.charAt(0).toUpperCase() + key.slice(1)}
                </button>
              ),
            )}
          </div>

          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <button
                type="button"
                disabled={bulkClosing}
                onClick={() => void handleBulkClose()}
                className="rounded-md bg-ink px-3 py-1.5 text-sm font-medium text-white hover:bg-ink/80 disabled:opacity-50"
              >
                {bulkClosing ? 'Closing...' : `Close ${String(selected.size)} selected`}
              </button>
            )}
            {openPeriods.length > 0 && (
              <button
                type="button"
                onClick={selectAllOpen}
                className="text-xs text-ink-muted hover:text-ink underline"
              >
                Select all open ({String(openPeriods.length)})
              </button>
            )}
          </div>
        </div>

        <Table
          sticky
          head={[
            { label: '' },
            { label: 'Week Ending' },
            { label: 'Date Range' },
            { label: 'Status' },
            { label: 'Entries', align: 'right' },
            { label: 'GP', align: 'right' },
            { label: 'ADP', align: 'right' },
            { label: 'Actions' },
          ]}
        >
          {filtered.length === 0 ? (
            <Row>
              <Cell muted>
                {periods.length === 0
                  ? 'No pay periods. Generate periods to get started.'
                  : 'No periods match the filter.'}
              </Cell>
            </Row>
          ) : (
            filtered.map((period) => {
              const hasData =
                period.timeEntryCount > 0 || period.gpRowCount > 0 || period.adpSubmissionCount > 0;
              const next = PERIOD_TRANSITIONS[period.status] ?? [];

              return (
                <Row key={period.id}>
                  <Cell>
                    {period.status !== 'closed' ? (
                      <input
                        type="checkbox"
                        checked={selected.has(period.id)}
                        onChange={() => toggleSelect(period.id)}
                        className="rounded border-gray-300"
                      />
                    ) : null}
                  </Cell>
                  <Cell>{period.weekEnding}</Cell>
                  <Cell muted>
                    {period.periodStart} – {period.periodEnd}
                  </Cell>
                  <Cell>
                    <Badge tone={periodStatusTone(period.status)}>
                      {period.status === 'in_review' ? 'In Review' : period.status}
                    </Badge>
                  </Cell>
                  <Cell align="right" mono>
                    {String(period.timeEntryCount)}
                  </Cell>
                  <Cell align="right" mono>
                    {String(period.gpRowCount)}
                  </Cell>
                  <Cell align="right" mono>
                    {String(period.adpSubmissionCount)}
                  </Cell>
                  <Cell>
                    <div className="flex items-center gap-1">
                      {next.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => void handleTransition(period.id, s)}
                          className="rounded border border-edge px-2 py-0.5 text-xs text-ink-muted hover:bg-surface-overlay hover:text-ink transition-colors"
                        >
                          {transitionLabel(s)}
                        </button>
                      ))}
                      {!hasData && period.status === 'open' && (
                        <button
                          type="button"
                          onClick={() => void handleDelete(period.id)}
                          className="rounded border border-red-200 px-2 py-0.5 text-xs text-red-500 hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </Cell>
                </Row>
              );
            })
          )}
        </Table>

        <div className="shrink-0 px-4 py-2 text-xs text-ink-muted border-t border-edge">
          {String(periods.length)} total periods —{' '}
          {String(periods.filter((p) => p.status === 'open').length)} open,{' '}
          {String(periods.filter((p) => p.status === 'closed').length)} closed
        </div>
      </Card>
    </div>
  );
}

/* ── Integrations Tab ──────────────────────────────────────────────────── */

const PROVIDER_LABELS: Record<string, { name: string; description: string }> = {
  oorwin: { name: 'Oorwin API', description: 'Timesheet and assignment data sync' },
  adp: { name: 'ADP API', description: 'Payroll submission and reconciliation' },
};

const PROVIDER_KEY_LABELS: Record<
  string,
  Record<string, { label: string; placeholder: string; sensitive: boolean }>
> = {
  oorwin: {
    base_url: {
      label: 'Base URL',
      placeholder: 'https://api.oorwinai.us/api/v2',
      sensitive: false,
    },
    api_token: {
      label: 'API Token',
      placeholder: 'Bearer token from POST /login',
      sensitive: true,
    },
  },
  adp: {
    client_id: { label: 'Client ID', placeholder: 'ADP OAuth client ID', sensitive: false },
    client_secret: {
      label: 'Client Secret',
      placeholder: 'ADP OAuth client secret',
      sensitive: true,
    },
    cert_pem: {
      label: 'Certificate (PEM)',
      placeholder: 'Paste the full certificate including BEGIN/END lines',
      sensitive: true,
    },
    key_pem: {
      label: 'Private Key (PEM)',
      placeholder: 'Paste the full private key including BEGIN/END lines',
      sensitive: true,
    },
  },
};

function IntegrationsTab() {
  const [providers, setProviders] = useState<IntegrationProviders | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingProvider, setEditingProvider] = useState<string | null>(null);

  const loadProviders = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.integrationStatus();
      setProviders(result);
    } catch {
      // Silently fail — cards will show loading state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  if (loading || providers === null) {
    return <Spinner label="Loading integrations…" />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {Object.entries(providers).map(([key, info]) => (
          <IntegrationCard
            key={key}
            provider={key}
            info={info}
            onConfigure={() => setEditingProvider(key)}
            onRefresh={loadProviders}
          />
        ))}
      </div>

      <Card className="px-5 py-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
          Database
        </p>
        <p className="mt-2 text-sm font-medium text-profit">Connected</p>
        <p className="mt-1 text-xs text-ink-faint">PostgreSQL 16</p>
      </Card>

      <SyncSection />

      <TimesheetHistorySection />

      {editingProvider !== null && (
        <IntegrationConfigModal
          provider={editingProvider}
          onClose={() => setEditingProvider(null)}
          onSaved={() => {
            setEditingProvider(null);
            void loadProviders();
          }}
        />
      )}
    </div>
  );
}

/* ── Sync Section ──────────────────────────────────────────────────────── */

function SyncSection() {
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const {
    data: runsData,
    error: runsError,
    reload: reloadRuns,
  } = useAsyncData<{ runs: SyncRunRow[] }>(
    () => api.syncRuns(),
    [],
    'Could not load sync history.',
  );

  const handleOorwinSync = async () => {
    setSyncing(true);
    setLastResult(null);
    try {
      const resp = await api.syncOorwin();
      const parts = resp.results.map(
        (r) =>
          `${r.entity}: ${String(r.created)} created, ${String(r.updated)} updated, ${String(r.skipped)} skipped`,
      );
      setLastResult(parts.join(' · '));
      reloadRuns();
    } catch (err) {
      setLastResult(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className="flex max-h-[min(28rem,calc(100vh-14rem))] flex-col">
      <CardHeader
        title="Data Sync"
        subtitle="Synchronise master data from external sources"
        aside={
          <button
            type="button"
            onClick={() => void handleOorwinSync()}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 rounded-lg bg-profit px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-profit/90 disabled:opacity-50"
          >
            {syncing ? (
              <>
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Syncing…
              </>
            ) : (
              'Sync Oorwin'
            )}
          </button>
        }
      />

      {lastResult !== null && (
        <div
          className={`mx-5 mb-3 rounded-lg px-3 py-2 text-sm ${
            lastResult.startsWith('Error')
              ? 'bg-caution/10 text-caution'
              : 'bg-profit/10 text-profit'
          }`}
        >
          {lastResult}
        </div>
      )}

      {runsError !== null && <p className="mx-5 mb-3 text-sm text-caution">{runsError}</p>}

      {runsData !== null && runsData.runs.length > 0 && (
        <Table
          sticky
          head={[
            { label: 'Source' },
            { label: 'Entity' },
            { label: 'Status' },
            { label: 'Created', align: 'right' },
            { label: 'Updated', align: 'right' },
            { label: 'Skipped', align: 'right' },
            { label: 'When', align: 'right' },
          ]}
        >
          {runsData.runs.slice(0, 20).map((run) => (
            <Row key={run.id}>
              <Cell>
                <Badge tone={run.source === 'oorwin' ? 'real' : 'warning'}>{run.source}</Badge>
              </Cell>
              <Cell>{run.entity}</Cell>
              <Cell>
                <Badge
                  tone={
                    run.status === 'completed'
                      ? 'real'
                      : run.status === 'failed'
                        ? 'neutral'
                        : 'warning'
                  }
                >
                  {run.status}
                </Badge>
              </Cell>
              <Cell align="right" mono>
                {run.recordsCreated}
              </Cell>
              <Cell align="right" mono>
                {run.recordsUpdated}
              </Cell>
              <Cell align="right" mono>
                {run.recordsSkipped}
              </Cell>
              <Cell align="right" mono>
                {formatDate(run.startedAt)}
              </Cell>
            </Row>
          ))}
        </Table>
      )}

      {runsData !== null && runsData.runs.length === 0 && (
        <p className="px-5 pb-4 text-sm text-ink-faint">No sync runs yet.</p>
      )}
    </Card>
  );
}

/* ── Oorwin Timesheet History ──────────────────────────────────────────── */

/** Before the earliest Oorwin timesheet (December 2020). */
const HISTORY_DEFAULT_FROM = '2020-01-01';
const HISTORY_POLL_MS = 3000;

function TimesheetHistorySection() {
  const [fromDate, setFromDate] = useState(HISTORY_DEFAULT_FROM);
  const [status, setStatus] = useState<OorwinSyncStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const {
    data: unplaced,
    error: unplacedError,
    reload: reloadUnplaced,
  } = useAsyncData<{ exceptions: PersistedException[] }>(
    () => api.organizationExceptions(),
    [],
    'Could not load timesheets that need a decision.',
  );

  const refresh = useCallback(async () => {
    try {
      setStatus(await api.oorwinSyncStatus());
      setStatusError(null);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Could not load the history status.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const backfill = status?.backfill ?? null;
  const running = backfill?.status === 'running';

  useEffect(() => {
    if (!running) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      await refresh();
      if (!cancelled) timer = setTimeout(() => void poll(), HISTORY_POLL_MS);
    };
    timer = setTimeout(() => void poll(), HISTORY_POLL_MS);
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [running, refresh]);

  const requestLoad = async (from: string | undefined) => {
    setRequesting(true);
    setNotice(null);
    try {
      const outcome = await api.loadOorwinHistory(from);
      if (outcome.outcome === 'busy') {
        setNotice({ tone: 'error', text: outcome.reason });
      } else {
        const text = {
          started: 'History load started. It runs in the background, so you can leave this page.',
          resumed: 'Resuming the history load where it stopped.',
          already_running: 'A history load is already running.',
        }[outcome.outcome];
        setNotice({ tone: 'ok', text });
      }
      await refresh();
    } catch (err) {
      setNotice({ tone: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setRequesting(false);
    }
  };

  const chunkSync = status?.currentSync?.trigger === 'backfill' ? status.currentSync : null;
  const pct =
    backfill !== null && backfill.chunksTotal > 0
      ? Math.round((backfill.chunksCompleted / backfill.chunksTotal) * 100)
      : 0;

  return (
    <Card>
      <CardHeader
        title="Oorwin Timesheet History"
        subtitle="Load approved Oorwin timesheets from a start date. Loads run in the background in parts of up to six months, and an interrupted load resumes where it stopped."
      />
      <div className="space-y-3 px-5 pb-4">
        {statusError !== null && <p className="text-sm text-caution">{statusError}</p>}

        {backfill !== null && running && (
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-ink">
                Loading part {Math.min(backfill.chunksCompleted + 1, backfill.chunksTotal)} of{' '}
                {backfill.chunksTotal}
              </span>
              <span className="text-xs text-ink-faint">
                {backfill.rowsFetched.toLocaleString()} rows so far
              </span>
            </div>
            {chunkSync?.windowStart != null && chunkSync.windowEnd != null && (
              <p className="mt-1 text-xs text-ink-faint">
                Now loading {formatDay(chunkSync.windowStart)} to {formatDay(chunkSync.windowEnd)}
              </p>
            )}
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-inset">
              <div
                className="h-full rounded-full bg-profit transition-all duration-300"
                style={{ width: `${String(pct)}%` }}
              />
            </div>
          </div>
        )}

        {backfill !== null && !running && (
          <p
            className={`text-sm ${backfill.status === 'failed' ? 'text-caution' : 'text-ink-muted'}`}
          >
            {backfill.status === 'completed'
              ? `Last load: ${formatDay(backfill.fromDate)} to ${formatDay(backfill.toDate)}, ${backfill.rowsFetched.toLocaleString()} rows, finished ${formatDate(backfill.completedAt)}.`
              : `The load from ${formatDay(backfill.fromDate)} stopped after ${String(backfill.chunksCompleted)} of ${String(backfill.chunksTotal)} parts: ${backfill.errorMessage ?? 'no reason was recorded'}.`}
          </p>
        )}

        {!running && (
          <div className="flex flex-wrap items-center gap-2">
            {backfill?.status === 'failed' && (
              <button
                type="button"
                onClick={() => void requestLoad(undefined)}
                disabled={requesting}
                className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
              >
                Resume
              </button>
            )}
            <label htmlFor="history-from" className="text-sm text-ink-muted">
              From
            </label>
            <input
              id="history-from"
              type="date"
              value={fromDate}
              max={localToday()}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded border border-edge bg-surface-base px-2 py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => void requestLoad(fromDate)}
              disabled={requesting || fromDate === ''}
              className="rounded border border-edge px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              {backfill === null ? 'Load history' : 'Load again'}
            </button>
          </div>
        )}

        {notice !== null && (
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              notice.tone === 'error' ? 'bg-caution/10 text-caution' : 'bg-profit/10 text-profit'
            }`}
          >
            {notice.text}
          </div>
        )}

        {unplacedError !== null && <p className="text-sm text-caution">{unplacedError}</p>}
        {unplaced !== null && unplaced.exceptions.length > 0 && (
          <div className="border-t border-edge pt-3">
            <p className="text-sm font-medium text-ink">
              Needs a decision ({unplaced.exceptions.length})
            </p>
            <p className="mt-0.5 text-xs text-ink-faint">
              Problems that belong to no single week, such as a timesheet spanning two. Problems
              tied to a week are listed with that week&apos;s exceptions.
            </p>
            <ul className="mt-2 space-y-2">
              {unplaced.exceptions.map((exception) => (
                <li key={exception.id} className="rounded-lg border border-edge px-3 py-2">
                  <p className="text-sm font-medium text-ink">{exception.title}</p>
                  <p className="mt-0.5 text-xs text-ink-muted">{exception.message}</p>
                  {resolvingId === exception.id ? (
                    <div className="mt-2">
                      <ResolveExceptionForm
                        exceptionId={exception.id}
                        onResolved={() => {
                          setResolvingId(null);
                          reloadUnplaced();
                        }}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setResolvingId(exception.id)}
                      className="mt-1 text-xs text-ink-muted underline hover:text-ink"
                    >
                      Resolve
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── Developer Tools Tab ─────────────────────────────────────────────── */

interface CatalogField {
  name: string;
  description: string;
  values?: string;
}

interface CatalogEndpoint {
  path: string;
  name: string;
  group: 'in_use' | 'discovery';
  description: string;
  defaultBody: Record<string, unknown>;
  fields: readonly CatalogField[];
}

function DeveloperToolsTab() {
  const [apiTab, setApiTab] = useState<'oorwin' | 'adp'>('oorwin');
  const [endpoints, setEndpoints] = useState<CatalogEndpoint[]>([]);
  const [selected, setSelected] = useState<CatalogEndpoint | null>(null);
  const [bodyText, setBodyText] = useState('{}');
  const [running, setRunning] = useState(false);
  const [fieldsExpanded, setFieldsExpanded] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    responseTimeMs: number;
    data: unknown;
    error?: string;
  } | null>(null);

  useEffect(() => {
    api
      .oorwinCatalog()
      .then((r) => {
        const eps = r.endpoints as CatalogEndpoint[];
        setEndpoints(eps);
        if (eps.length > 0 && selected === null) {
          setSelected(eps[0]!);
          setBodyText(JSON.stringify(eps[0]!.defaultBody, null, 2));
        }
      })
      .catch(() => {});
  }, []);

  function selectEndpoint(path: string) {
    const ep = endpoints.find((e) => e.path === path);
    if (ep) {
      setSelected(ep);
      setBodyText(JSON.stringify(ep.defaultBody, null, 2));
      setResult(null);
      setFieldsExpanded(false);
    }
  }

  async function execute() {
    if (!selected) return;
    setRunning(true);
    setResult(null);
    try {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(bodyText) as Record<string, unknown>;
      } catch {
        setResult({
          ok: false,
          responseTimeMs: 0,
          data: null,
          error: 'Invalid JSON in request body',
        });
        return;
      }
      const res = await api.oorwinProxy(selected.path, parsed);
      setResult(res);
    } catch (e) {
      setResult({
        ok: false,
        responseTimeMs: 0,
        data: null,
        error: e instanceof Error ? e.message : 'Request failed',
      });
    } finally {
      setRunning(false);
    }
  }

  const inUse = endpoints.filter((e) => e.group === 'in_use');
  const discovery = endpoints.filter((e) => e.group === 'discovery');

  /** Extract the data array from any Oorwin response shape. */
  function extractRows(data: unknown): Record<string, unknown>[] | null {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (d['data'] && typeof d['data'] === 'object') {
      const inner = d['data'] as Record<string, unknown>;
      if (Array.isArray(inner['list'])) return inner['list'] as Record<string, unknown>[];
      if (inner['timesheets'] && typeof inner['timesheets'] === 'object') {
        const ts = inner['timesheets'] as Record<string, unknown>;
        if (Array.isArray(ts['data'])) return ts['data'] as Record<string, unknown>[];
      }
    }
    return null;
  }

  const dataRows = result?.data ? extractRows(result.data) : null;
  const rowCount = dataRows?.length ?? null;

  return (
    <div className="space-y-4">
      <div className="flex gap-1 rounded-lg border border-edge bg-surface-base p-0.5 w-fit">
        {(
          [
            ['oorwin', 'Oorwin'],
            ['adp', 'ADP'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setApiTab(key)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              apiTab === key ? 'bg-surface-overlay text-ink' : 'text-ink-faint hover:text-ink-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {apiTab === 'oorwin' && (
        <>
          <Card>
            <CardHeader title="Oorwin API Explorer" />
            <div className="px-4 pb-4">
              <p className="text-xs text-ink-faint mb-4">
                Test Oorwin API endpoints using the application&apos;s stored credentials. Read-only
                — no data is written to the database.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left: endpoint picker + body */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-ink-muted mb-1">
                      Endpoint
                    </label>
                    <select
                      value={selected?.path ?? ''}
                      onChange={(e) => selectEndpoint(e.target.value)}
                      className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm"
                    >
                      <optgroup label="Verified Working">
                        {inUse.map((e) => (
                          <option key={e.path} value={e.path}>
                            {e.name} — {e.path}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Unverified — paths may not exist">
                        {discovery.map((e) => (
                          <option key={e.path} value={e.path}>
                            {e.name} — {e.path}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {selected && (
                    <div className="rounded-lg bg-surface-overlay/50 border border-edge/50 px-3 py-2">
                      <p className="text-xs text-ink-muted">{selected.description}</p>
                      {selected.fields.length > 0 && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setFieldsExpanded(!fieldsExpanded)}
                            className="flex items-center gap-1 text-[11px] font-medium text-ink-faint hover:text-ink-muted transition-colors"
                          >
                            <svg
                              className={`h-3 w-3 transition-transform ${fieldsExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                            Field Reference ({String(selected.fields.length)} parameters)
                          </button>
                          {fieldsExpanded && (
                            <div className="mt-2 space-y-1.5">
                              {selected.fields.map((f) => (
                                <div key={f.name} className="flex gap-2 text-[11px]">
                                  <code className="shrink-0 rounded bg-surface-base px-1.5 py-0.5 font-mono text-ink">
                                    {f.name}
                                  </code>
                                  <span className="text-ink-faint">
                                    {f.description}
                                    {f.values && (
                                      <span className="ml-1 text-ink-muted">
                                        Values: <code className="font-mono">{f.values}</code>
                                      </span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-ink-muted mb-1">
                      Request Body (JSON)
                    </label>
                    <textarea
                      value={bodyText}
                      onChange={(e) => setBodyText(e.target.value)}
                      rows={12}
                      spellCheck={false}
                      className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-xs font-mono text-ink leading-relaxed resize-y"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={running || !selected}
                      onClick={() => void execute()}
                      className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                        running
                          ? 'bg-surface-overlay text-ink-faint cursor-wait'
                          : 'bg-profit text-white hover:bg-profit/90'
                      }`}
                    >
                      {running ? 'Executing…' : 'Execute'}
                    </button>
                    {selected && (
                      <button
                        type="button"
                        onClick={() => {
                          setBodyText(JSON.stringify(selected.defaultBody, null, 2));
                          setResult(null);
                        }}
                        className="text-xs text-ink-muted underline hover:text-ink"
                      >
                        Reset to defaults
                      </button>
                    )}
                  </div>
                </div>

                {/* Right: response */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-ink-muted">Response</label>
                  {result === null ? (
                    <div className="rounded-lg border border-edge bg-surface-overlay/30 px-4 py-8 text-center text-xs text-ink-faint">
                      Pick an endpoint and click Execute to see the response.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 text-xs">
                        <span
                          className={`font-medium ${result.ok ? 'text-profit' : 'text-red-600'}`}
                        >
                          {result.ok ? 'Success' : 'Error'}
                        </span>
                        <span className="text-ink-faint">{result.responseTimeMs}ms</span>
                        {rowCount !== null && (
                          <span className="text-ink-faint">
                            {rowCount} row{rowCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {result.error && (
                        <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                          {result.error}
                        </div>
                      )}
                      <textarea
                        readOnly
                        value={JSON.stringify(result.data, null, 2) ?? ''}
                        rows={20}
                        className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-xs font-mono text-ink leading-relaxed resize-y"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {result !== null && dataRows !== null && dataRows.length === 0 && (
            <Card>
              <div className="px-4 py-6 text-center text-xs text-ink-faint">
                No data rows in the response. Try increasing the limit or adjusting the filters.
              </div>
            </Card>
          )}

          {dataRows !== null && dataRows.length > 0 && (
            <Card>
              <CardHeader title={`Data Grid (${String(dataRows.length)} rows)`} />
              <div className="overflow-auto max-h-[500px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-surface-overlay border-b border-edge">
                      {Object.keys(dataRows[0]!)
                        .filter((k) => {
                          const v = dataRows[0]![k];
                          return v === null || typeof v !== 'object';
                        })
                        .map((key) => (
                          <th
                            key={key}
                            className="px-3 py-2 text-left font-medium text-ink-muted whitespace-nowrap"
                          >
                            {key}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dataRows.map((row, i) => {
                      const visibleKeys = Object.keys(row).filter((k) => {
                        const v = dataRows[0]![k];
                        return v === null || typeof v !== 'object';
                      });
                      return (
                        <tr
                          key={i}
                          className={`border-b border-edge/30 ${i % 2 === 1 ? 'bg-surface-overlay/20' : ''}`}
                        >
                          {visibleKeys.map((key) => {
                            const val = row[key];
                            const display =
                              val === null || val === undefined
                                ? '—'
                                : typeof val === 'boolean'
                                  ? val
                                    ? 'true'
                                    : 'false'
                                  : String(val);
                            return (
                              <td
                                key={key}
                                className="px-3 py-1.5 text-ink whitespace-nowrap max-w-[300px] truncate"
                                title={display}
                              >
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {apiTab === 'adp' && <AdpExplorerCard />}
    </div>
  );
}

function AdpExplorerCard() {
  const [endpoints, setEndpoints] = useState<
    {
      path: string;
      method: string;
      name: string;
      group: string;
      category?: string;
      description: string;
      defaultQuery: Record<string, string>;
      fields: readonly { name: string; description: string; values?: string }[];
    }[]
  >([]);
  const [selected, setSelected] = useState<(typeof endpoints)[number] | null>(null);
  const [queryText, setQueryText] = useState('{}');
  const [pathText, setPathText] = useState('');
  const [running, setRunning] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    responseTimeMs: number;
    data: unknown;
    error?: string;
  } | null>(null);

  useEffect(() => {
    api
      .adpCatalog()
      .then((r) => {
        const eps = r.endpoints.filter((e) => e.group === 'read') as typeof endpoints;
        setEndpoints(eps);
        if (eps.length > 0 && selected === null) {
          setSelected(eps[0]!);
          setPathText(eps[0]!.path);
          setQueryText(JSON.stringify(eps[0]!.defaultQuery, null, 2));
        }
      })
      .catch(() => {});
  }, []);

  function selectEndpoint(path: string) {
    const ep = endpoints.find((e) => e.path === path);
    if (ep) {
      setSelected(ep);
      setPathText(ep.path);
      setQueryText(JSON.stringify(ep.defaultQuery, null, 2));
      setResult(null);
      setFieldsOpen(false);
    }
  }

  async function execute() {
    if (!selected) return;
    setRunning(true);
    setResult(null);
    try {
      let params: Record<string, string>;
      try {
        params = JSON.parse(queryText) as Record<string, string>;
      } catch {
        setResult({
          ok: false,
          responseTimeMs: 0,
          data: null,
          error: 'Invalid JSON in query parameters',
        });
        return;
      }
      const res = await api.adpProxy(pathText, params);
      setResult(res);
    } catch (e) {
      setResult({
        ok: false,
        responseTimeMs: 0,
        data: null,
        error: e instanceof Error ? e.message : 'Request failed',
      });
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card>
      <CardHeader title="ADP API Explorer" />
      <div className="px-4 pb-4">
        <p className="text-xs text-ink-faint mb-4">
          Test ADP API endpoints using the stored credentials. Read-only — write endpoints are
          blocked. Uses OAuth 2.0 + mutual TLS authentication automatically.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">Endpoint</label>
              <select
                value={selected?.path ?? ''}
                onChange={(e) => selectEndpoint(e.target.value)}
                className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm"
              >
                {(() => {
                  const categories = [...new Set(endpoints.map((e) => e.category ?? 'Other'))];
                  return categories.map((cat) => (
                    <optgroup key={cat} label={cat}>
                      {endpoints
                        .filter((e) => (e.category ?? 'Other') === cat)
                        .map((e) => (
                          <option key={e.path} value={e.path}>
                            {e.name} — {e.method} {e.path}
                          </option>
                        ))}
                    </optgroup>
                  ));
                })()}
              </select>
            </div>

            {selected && (
              <div className="rounded-lg bg-surface-overlay/50 border border-edge/50 px-3 py-2">
                <p className="text-xs text-ink-muted">{selected.description}</p>
                {selected.fields.length > 0 && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setFieldsOpen(!fieldsOpen)}
                      className="flex items-center gap-1 text-[11px] font-medium text-ink-faint hover:text-ink-muted transition-colors"
                    >
                      <svg
                        className={`h-3 w-3 transition-transform ${fieldsOpen ? 'rotate-90' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                      Field Reference ({String(selected.fields.length)})
                    </button>
                    {fieldsOpen && (
                      <div className="mt-2 space-y-1.5">
                        {selected.fields.map((f) => (
                          <div key={f.name} className="flex gap-2 text-[11px]">
                            <code className="shrink-0 rounded bg-surface-base px-1.5 py-0.5 font-mono text-ink">
                              {f.name}
                            </code>
                            <span className="text-ink-faint">
                              {f.description}
                              {f.values && (
                                <span className="ml-1 text-ink-muted">
                                  Values: <code className="font-mono">{f.values}</code>
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">
                Path (editable for aoid substitution)
              </label>
              <input
                type="text"
                value={pathText}
                onChange={(e) => setPathText(e.target.value)}
                className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-sm font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">
                Query Parameters (JSON)
              </label>
              <textarea
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                rows={4}
                spellCheck={false}
                className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-xs font-mono text-ink leading-relaxed resize-y"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={running || !selected}
                onClick={() => void execute()}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  running
                    ? 'bg-surface-overlay text-ink-faint cursor-wait'
                    : 'bg-profit text-white hover:bg-profit/90'
                }`}
              >
                {running ? 'Executing…' : 'Execute'}
              </button>
              {selected && (
                <button
                  type="button"
                  onClick={() => {
                    setPathText(selected.path);
                    setQueryText(JSON.stringify(selected.defaultQuery, null, 2));
                    setResult(null);
                  }}
                  className="text-xs text-ink-muted underline hover:text-ink"
                >
                  Reset to defaults
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-ink-muted">Response</label>
            {result === null ? (
              <div className="rounded-lg border border-edge bg-surface-overlay/30 px-4 py-8 text-center text-xs text-ink-faint">
                Pick an endpoint and click Execute to test the ADP connection.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 text-xs">
                  <span className={`font-medium ${result.ok ? 'text-profit' : 'text-red-600'}`}>
                    {result.ok ? 'Success' : 'Error'}
                  </span>
                  <span className="text-ink-faint">{result.responseTimeMs}ms</span>
                </div>
                {result.error && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                    {result.error}
                  </div>
                )}
                <textarea
                  readOnly
                  value={JSON.stringify(result.data, null, 2) ?? ''}
                  rows={20}
                  className="w-full rounded-lg border border-edge bg-surface-base px-3 py-2 text-xs font-mono text-ink leading-relaxed resize-y"
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Data Grid */}
      {result !== null &&
        result.data !== null &&
        (() => {
          const data = result.data as Record<string, unknown>;
          let rows: Record<string, unknown>[] | null = null;
          // ADP responses vary: workers are in data.workers, codelists in data.codeLists, etc.
          if (Array.isArray(data['workers'])) rows = data['workers'] as Record<string, unknown>[];
          else if (Array.isArray(data['codeLists']))
            rows = data['codeLists'] as Record<string, unknown>[];
          else if (Array.isArray(data['costCenters']))
            rows = data['costCenters'] as Record<string, unknown>[];
          else if (Array.isArray(data['departments']))
            rows = data['departments'] as Record<string, unknown>[];
          else if (Array.isArray(data['payrollGroups']))
            rows = data['payrollGroups'] as Record<string, unknown>[];
          else if (Array.isArray(data['payrollGroupsPayPeriods']))
            rows = data['payrollGroupsPayPeriods'] as Record<string, unknown>[];
          else if (Array.isArray(data['listItems']))
            rows = data['listItems'] as Record<string, unknown>[];
          else if (Array.isArray(data['eventNotificationMessages']))
            rows = data['eventNotificationMessages'] as Record<string, unknown>[];
          else if (data['meta'] && typeof data['meta'] === 'object')
            rows = [data['meta'] as Record<string, unknown>];
          // Try generic: any top-level array
          if (rows === null) {
            for (const val of Object.values(data)) {
              if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
                rows = val as Record<string, unknown>[];
                break;
              }
            }
          }
          if (rows === null || rows.length === 0) return null;

          /**
           * Recursively flatten a nested object into dot-notated scalar columns.
           * ADP wraps nearly everything in objects like {codeValue:"Active"}.
           * This turns {person:{genderCode:{codeValue:"M"}}} into {"person.genderCode":"M"}.
           */
          /**
           * Extract a scalar label from an ADP nameCode field.
           * nameCode can be a string or {codeValue: "X", ...}.
           */
          const extractNameCode = (val: unknown): string | null => {
            if (typeof val === 'string') return val;
            if (val && typeof val === 'object' && 'codeValue' in (val as Record<string, unknown>)) {
              return String((val as Record<string, unknown>)['codeValue']);
            }
            return null;
          };

          const flattenRow = (
            obj: Record<string, unknown>,
            prefix = '',
          ): Record<string, string> => {
            const out: Record<string, string> = {};
            for (const [key, val] of Object.entries(obj)) {
              const path = prefix ? `${prefix}.${key}` : key;
              if (val === null || val === undefined) {
                out[path] = '—';
              } else if (typeof val === 'boolean') {
                out[path] = val ? 'true' : 'false';
              } else if (typeof val === 'string' || typeof val === 'number') {
                out[path] = String(val);
              } else if (Array.isArray(val)) {
                if (val.length === 0) {
                  out[path] = '—';
                } else if (typeof val[0] === 'object' && val[0] !== null) {
                  const sub = flattenRow(val[0] as Record<string, unknown>, `${path}[0]`);
                  Object.assign(out, sub);
                  if (val.length > 1) out[`${path}.count`] = String(val.length);
                } else {
                  out[path] = val.map(String).join(', ');
                }
              } else if (typeof val === 'object') {
                const inner = val as Record<string, unknown>;
                // ADP code-object shortcut: {codeValue:"X",...} → just "X"
                if ('codeValue' in inner && Object.keys(inner).length <= 3) {
                  out[path] = String(inner['codeValue']);
                } else {
                  // ADP nameCode pattern: nameCode labels the sibling fields.
                  // Use its value as the group prefix, drop the nameCode column.
                  const label = 'nameCode' in inner ? extractNameCode(inner['nameCode']) : null;
                  const groupPrefix = label ?? path;
                  // Count non-nameCode value fields
                  const valueKeys = Object.keys(inner).filter((k) => k !== 'nameCode' || !label);
                  const singleValue = label && valueKeys.length === 1;
                  for (const [innerKey, innerVal] of Object.entries(inner)) {
                    if (innerKey === 'nameCode' && label) continue; // skip — promoted to header
                    // If only one value field in the group, use just the label as the column
                    const innerPath = singleValue ? groupPrefix : `${groupPrefix}.${innerKey}`;
                    if (innerVal === null || innerVal === undefined) {
                      out[innerPath] = '—';
                    } else if (typeof innerVal === 'string' || typeof innerVal === 'number') {
                      out[innerPath] = String(innerVal);
                    } else if (typeof innerVal === 'boolean') {
                      out[innerPath] = innerVal ? 'true' : 'false';
                    } else if (Array.isArray(innerVal)) {
                      if (innerVal.length === 0) out[innerPath] = '—';
                      else if (typeof innerVal[0] === 'object' && innerVal[0] !== null) {
                        Object.assign(
                          out,
                          flattenRow(innerVal[0] as Record<string, unknown>, `${innerPath}[0]`),
                        );
                        if (innerVal.length > 1)
                          out[`${innerPath}.count`] = String(innerVal.length);
                      } else {
                        out[innerPath] = innerVal.map(String).join(', ');
                      }
                    } else if (typeof innerVal === 'object') {
                      const sub = innerVal as Record<string, unknown>;
                      if ('codeValue' in sub && Object.keys(sub).length <= 3) {
                        out[innerPath] = String(sub['codeValue']);
                      } else {
                        Object.assign(out, flattenRow(sub, innerPath));
                      }
                    }
                  }
                }
              }
            }
            return out;
          };

          const flatRows = rows.map((r) => flattenRow(r));

          // Collect all column keys across every row (preserving first-seen order)
          const colSet = new Set<string>();
          for (const fr of flatRows) {
            for (const k of Object.keys(fr)) colSet.add(k);
          }
          const columns = Array.from(colSet);

          // Column headers: use the shortest unique suffix for each column.
          // If multiple columns end with the same segment (e.g. two "nameCode"),
          // include enough parent context to disambiguate.
          const buildLabels = (cols: string[]): Map<string, string> => {
            const labels = new Map<string, string>();
            // Start with the last segment
            for (const col of cols) {
              const parts = col.replace(/\[\d+]/g, '').split('.');
              labels.set(col, parts[parts.length - 1]!);
            }
            // Detect duplicates and lengthen them
            let changed = true;
            while (changed) {
              changed = false;
              const seen = new Map<string, string[]>();
              for (const [col, label] of labels) {
                const arr = seen.get(label) ?? [];
                arr.push(col);
                seen.set(label, arr);
              }
              for (const [, dupes] of seen) {
                if (dupes.length <= 1) continue;
                for (const col of dupes) {
                  const parts = col.replace(/\[\d+]/g, '').split('.');
                  const curLabel = labels.get(col)!;
                  const curSegments = curLabel.split('.');
                  if (curSegments.length < parts.length) {
                    // Prepend one more parent segment
                    const idx = parts.length - curSegments.length - 1;
                    labels.set(col, `${parts[idx]}.${curLabel}`);
                    changed = true;
                  }
                }
              }
            }
            return labels;
          };
          const columnLabels = buildLabels(columns);

          return (
            <Card>
              <CardHeader title={`Data Grid (${String(rows.length)} rows)`} />
              <div className="overflow-auto max-h-[500px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-surface-overlay border-b border-edge">
                      {columns.map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left font-medium text-ink-muted whitespace-nowrap"
                          title={col}
                        >
                          {columnLabels.get(col) ?? col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flatRows.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-b border-edge/30 ${i % 2 === 1 ? 'bg-surface-overlay/20' : ''}`}
                      >
                        {columns.map((col) => {
                          const display = row[col] ?? '—';
                          return (
                            <td
                              key={col}
                              className="px-3 py-1.5 text-ink whitespace-nowrap max-w-[300px] truncate"
                              title={display}
                            >
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })()}
    </Card>
  );
}

/* ── Verification Thresholds Card ────────────────────────────────────── */

function VerificationThresholdsCard() {
  const { data: configData, reload: reloadConfig } = useAsyncData<PeriodConfigResponse>(
    () => api.periodConfig(),
    [],
    'Could not load period config.',
  );

  const [minHoursDraft, setMinHoursDraft] = useState<string>(
    String(configData?.verifyMinHours ?? 40),
  );
  const [saving, setSaving] = useState(false);

  // Keep draft in sync when config reloads
  useEffect(() => {
    if (configData !== null) setMinHoursDraft(String(configData.verifyMinHours));
  }, [configData?.verifyMinHours]);

  async function saveMinHours() {
    const val = Number(minHoursDraft);
    if (isNaN(val) || val <= 0 || val > 168) return;
    setSaving(true);
    try {
      await api.updatePeriodConfig({ verifyMinHours: val });
      reloadConfig();
    } finally {
      setSaving(false);
    }
  }

  if (configData === null) {
    return (
      <Card>
        <CardHeader title="Verification Thresholds" />
        <p className="px-4 pb-3 text-sm text-ink-muted">Loading…</p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Verification Thresholds" />
      <div className="px-4 pb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={configData.verifyFlaggingEnabled}
              onChange={async (e) => {
                await api.updatePeriodConfig({ verifyFlaggingEnabled: e.target.checked });
                reloadConfig();
              }}
              className="h-4 w-4 rounded border-edge accent-profit"
            />
            Enable automatic flagging
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            Minimum expected hours
            <input
              type="number"
              min="1"
              max="168"
              step="1"
              value={minHoursDraft}
              onChange={(e) => setMinHoursDraft(e.target.value)}
              onBlur={() => void saveMinHours()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveMinHours();
              }}
              disabled={saving}
              className="w-16 rounded border border-edge bg-surface-base px-2 py-1 text-sm text-center disabled:opacity-50"
            />
            per week
          </label>
        </div>
        <p className="mt-2 text-xs text-ink-faint">
          Contractors with fewer billable hours (regular + overtime) than the minimum, and no
          holiday or vacation time to explain it, are flagged for review on the Verify Hours step.
        </p>
      </div>
    </Card>
  );
}

/* ── Integration Card ───────────────────────────────────────────────────── */

function IntegrationCard({
  provider,
  info,
  onConfigure,
  onRefresh,
}: {
  provider: string;
  info: IntegrationProviders['oorwin'];
  onConfigure: () => void;
  onRefresh: () => void;
}) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<IntegrationTestResult | null>(null);

  const meta = PROVIDER_LABELS[provider] ?? { name: provider, description: '' };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.testIntegration(provider);
      setTestResult(result);
    } catch (e) {
      setTestResult({
        provider,
        status: 'error',
        message: e instanceof Error ? e.message : 'Test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const statusColor =
    testResult !== null
      ? testResult.status === 'connected'
        ? 'text-profit'
        : testResult.status === 'error'
          ? 'text-caution'
          : 'text-ink-muted'
      : info.configured
        ? 'text-profit'
        : 'text-ink-muted';

  const statusText =
    testResult !== null
      ? testResult.status === 'connected'
        ? 'Connected'
        : testResult.status === 'error'
          ? 'Error'
          : 'Not Configured'
      : info.configured
        ? 'Configured'
        : 'Not Configured';

  const sourceLabel = '';

  return (
    <Card className="px-5 py-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            {meta.name}
          </p>
          <p className={`mt-2 text-sm font-medium ${statusColor}`}>{statusText}</p>
          <p className="mt-1 text-xs text-ink-faint">{meta.description}</p>
          {sourceLabel !== '' && (
            <p className="mt-1 text-[11px] text-ink-faint italic">{sourceLabel}</p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => void handleTest()}
            disabled={testing || !info.configured}
            className="rounded border border-edge px-3 py-1.5 text-xs font-medium text-ink-muted
                       hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {testing ? 'Testing…' : 'Test'}
          </button>
          <button
            onClick={onConfigure}
            className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90"
          >
            Configure
          </button>
        </div>
      </div>

      {testResult !== null && (
        <div
          className={`mt-3 rounded p-2.5 text-xs ${
            testResult.status === 'connected'
              ? 'bg-profit/10 text-profit'
              : testResult.status === 'error'
                ? 'bg-caution/10 text-caution'
                : 'bg-surface-inset text-ink-muted'
          }`}
        >
          {testResult.message}
          <button
            onClick={() => {
              setTestResult(null);
              onRefresh();
            }}
            className="ml-2 underline opacity-60 hover:opacity-100"
          >
            dismiss
          </button>
        </div>
      )}
    </Card>
  );
}

/* ── Integration Config Modal ──────────────────────────────────────────── */

function IntegrationConfigModal({
  provider,
  onClose,
  onSaved,
}: {
  provider: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [entries, setEntries] = useState<readonly IntegrationConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const meta = PROVIDER_LABELS[provider] ?? { name: provider, description: '' };
  const keyConfig = PROVIDER_KEY_LABELS[provider] ?? {};

  useEffect(() => {
    void (async () => {
      try {
        const result = await api.integrationConfig(provider);
        setEntries(result.entries);
        // Pre-fill values from existing entries (masked values are placeholders)
        const initial: Record<string, string> = {};
        for (const entry of result.entries) {
          initial[entry.key] = entry.isSensitive ? '' : entry.value;
        }
        setValues(initial);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load config');
      } finally {
        setLoading(false);
      }
    })();
  }, [provider]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only send non-empty values (empty = no change for sensitive, delete for non-sensitive)
      const toSave: Record<string, string> = {};
      for (const [key, value] of Object.entries(values)) {
        if (value.trim() !== '') {
          toSave[key] = value.trim();
        }
      }
      if (Object.keys(toSave).length === 0) {
        setError('No changes to save.');
        setSaving(false);
        return;
      }
      await api.saveIntegrationConfig(provider, toSave);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Build the list of all keys for this provider
  const allKeys = Object.keys(keyConfig);

  return (
    <Modal open title={`Configure ${meta.name}`} onClose={onClose} size="lg">
      {loading ? (
        <Spinner label="Loading configuration…" />
      ) : (
        <div className="space-y-4">
          {allKeys.map((key) => {
            const cfg = keyConfig[key]!;
            const existingEntry = entries.find((e) => e.key === key);
            const hasValue = existingEntry !== undefined;

            return (
              <div key={key}>
                <label className="block text-xs font-medium text-ink-muted mb-1">
                  {cfg.label}
                  {hasValue && <span className="ml-2 text-[11px] text-profit">(configured)</span>}
                </label>
                <div className="flex gap-2">
                  <input
                    type={cfg.sensitive && showSecrets[key] !== true ? 'password' : 'text'}
                    placeholder={
                      hasValue && cfg.sensitive
                        ? `Current: ${existingEntry.value}`
                        : cfg.placeholder
                    }
                    value={values[key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="flex-1 rounded border border-edge bg-white px-3 py-2 text-sm
                               text-ink placeholder:text-ink-faint/50 focus:border-accent
                               focus:outline-none focus:ring-1 focus:ring-accent/30"
                  />
                  {cfg.sensitive && (
                    <button
                      type="button"
                      onClick={() => setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }))}
                      className="rounded border border-edge px-2 py-2 text-xs text-ink-muted
                                 hover:bg-surface-hover"
                      title={showSecrets[key] === true ? 'Hide' : 'Show'}
                    >
                      {showSecrets[key] === true ? '🔒' : '👁'}
                    </button>
                  )}
                </div>
                {hasValue && cfg.sensitive && (
                  <p className="mt-1 text-[11px] text-ink-faint">
                    Leave blank to keep current value. Enter a new value to update.
                  </p>
                )}
              </div>
            );
          })}

          {error !== null && <p className="text-xs text-caution">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="rounded border border-edge px-4 py-2 text-sm text-ink-muted
                         hover:bg-surface-hover"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="rounded bg-accent px-4 py-2 text-sm font-medium text-white
                         hover:bg-accent/90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Commission Plans Card (multi-plan, multi-schedule, unified grid) ── */

/** Derive display-friendly ranges from sorted bracket thresholds. */
function bracketRanges(
  brackets: readonly { id: string; threshold: string }[],
): { id: string; fromLabel: string; toLabel: string }[] {
  const sorted = [...brackets].sort((a, b) => Number(a.threshold) - Number(b.threshold));
  return sorted.map((b, i) => {
    const from = Number(b.threshold);
    const next = sorted[i + 1];
    const to = next ? Number(next.threshold) - 1 : null;
    return {
      id: b.id,
      fromLabel: `$${from.toLocaleString()}`,
      toLabel: to !== null ? `$${to.toLocaleString()}` : '\u2014',
    };
  });
}

interface PlanEditState {
  name: string;
  /** keyed by scheduleId → array of { tierId, rate } in bracket order */
  rates: Record<string, { tierId: string; rate: string }[]>;
}

function CommissionPlansCard() {
  const { data, error, loading, reload } = useAsyncData(
    () => api.getCommissionPlans(),
    [],
    'Could not load commission plans.',
  );

  // Per-plan edit state, keyed by planId
  const [editing, setEditing] = useState<Record<string, PlanEditState>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Add-plan inline form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTierMode, setNewTierMode] = useState<'marginal' | 'flat'>('marginal');
  const [newElevatedMonths, setNewElevatedMonths] = useState('18');
  const [creating, setCreating] = useState(false);

  const startEditing = (plan: NonNullable<typeof data>['plans'][number]) => {
    const rates: Record<string, { tierId: string; rate: string }[]> = {};
    for (const sched of plan.schedules) {
      rates[sched.id] = plan.brackets.map((b) => {
        const existing = sched.rates.find((r) => r.tierId === b.id);
        return { tierId: b.id, rate: existing?.rate ?? '0' };
      });
    }
    setEditing((prev) => ({ ...prev, [plan.id]: { name: plan.name, rates } }));
  };

  const cancelEditing = (planId: string) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
    setSaveError(null);
  };

  const handleSave = async (plan: NonNullable<typeof data>['plans'][number]) => {
    const state = editing[plan.id];
    if (!state) return;
    setSaving(plan.id);
    setSaveError(null);
    try {
      // Save name if changed
      if (state.name !== plan.name) {
        await api.updateCommissionPlan(plan.id, { name: state.name });
      }
      // Save rates for each schedule that changed
      for (const sched of plan.schedules) {
        const edited = state.rates[sched.id];
        if (!edited) continue;
        const changed = edited.some((r) => {
          const orig = sched.rates.find((o) => o.tierId === r.tierId);
          return orig?.rate !== r.rate;
        });
        if (changed) {
          await api.updateScheduleRates(plan.id, sched.id, edited);
        }
      }
      cancelEditing(plan.id);
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (planId: string) => {
    if (!confirm('Delete this commission plan? This cannot be undone.')) return;
    setDeleting(planId);
    setSaveError(null);
    try {
      await api.deleteCommissionPlan(planId);
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setSaveError(null);
    try {
      await api.createCommissionPlan({
        name: newName.trim(),
        tierMode: newTierMode,
        elevatedPeriodMonths: Number(newElevatedMonths) || 18,
      });
      setShowAddForm(false);
      setNewName('');
      setNewTierMode('marginal');
      setNewElevatedMonths('18');
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  if (error)
    return (
      <Card>
        <CardHeader title="Commission Plans" />
        <p className="px-4 pb-3 text-sm text-red-600">{error}</p>
      </Card>
    );
  if (loading || !data)
    return (
      <Card>
        <CardHeader title="Commission Plans" />
        <p className="px-4 pb-3 text-sm text-ink-muted">Loading…</p>
      </Card>
    );

  return (
    <Card>
      <CardHeader
        title="Commission Plans"
        subtitle={`${String(data.plans.length)} plan${data.plans.length !== 1 ? 's' : ''} configured`}
      />

      {data.plans.length === 0 && !showAddForm && (
        <p className="px-4 pb-3 text-sm text-ink-muted">No commission plans configured.</p>
      )}

      <div className="grid grid-cols-1 gap-4 px-4 pb-4 lg:grid-cols-2 xl:grid-cols-3">
        {data.plans.map((plan) => {
          const editState = editing[plan.id];
          const isEditing = !!editState;
          const isSaving = saving === plan.id;
          const isDeleting = deleting === plan.id;
          const ranges = bracketRanges(plan.brackets);
          const canDelete = plan.activeAllocations === 0;

          return (
            <div
              key={plan.id}
              className="rounded-lg border border-edge bg-surface-raised p-3 space-y-3"
            >
              {/* Plan header */}
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editState.name}
                      onChange={(e) =>
                        setEditing((prev) => ({
                          ...prev,
                          [plan.id]: { ...prev[plan.id]!, name: e.target.value },
                        }))
                      }
                      className="w-full rounded border border-edge bg-surface px-2 py-1 text-sm font-semibold text-ink"
                    />
                  ) : (
                    <h4 className="text-sm font-semibold text-ink">{plan.name}</h4>
                  )}
                  <p className="text-xs text-ink-muted mt-0.5">
                    {plan.tierMode} mode · {String(plan.elevatedPeriodMonths)} month elevated window
                    {plan.activeAllocations > 0 && (
                      <span className="ml-2 text-ink-faint">
                        · {String(plan.activeAllocations)} active assignment
                        {plan.activeAllocations !== 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void handleSave(plan)}
                        disabled={isSaving}
                        className="rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
                      >
                        {isSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelEditing(plan.id)}
                        disabled={isSaving}
                        className="text-xs text-ink-faint hover:text-ink-muted"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => startEditing(plan)}
                        className="text-xs text-profit hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(plan.id)}
                        disabled={!canDelete || isDeleting}
                        title={
                          canDelete
                            ? 'Delete plan'
                            : `Assigned to ${String(plan.activeAllocations)} allocation(s)`
                        }
                        className="text-xs text-red-500 hover:underline disabled:opacity-30 disabled:cursor-not-allowed disabled:no-underline"
                      >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Unified grid: From | To | Schedule1 | Schedule2 | ... */}
              {plan.brackets.length > 0 && plan.schedules.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-edge text-left">
                        <th className="py-1.5 pr-3 font-medium text-ink-muted">From</th>
                        <th className="py-1.5 pr-4 font-medium text-ink-muted">To</th>
                        {plan.schedules.map((sched) => (
                          <th key={sched.id} className="py-1.5 pr-3 font-medium text-ink-muted">
                            <span>{sched.name}</span>
                            {sched.isElevated && (
                              <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                                ELEVATED
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ranges.map((range, rowIdx) => (
                        <tr key={range.id} className="border-b border-edge/50">
                          <td className="py-1.5 pr-3 font-mono text-ink">{range.fromLabel}</td>
                          <td className="py-1.5 pr-4 font-mono text-ink">{range.toLabel}</td>
                          {plan.schedules.map((sched) => {
                            const bracket = plan.brackets.find((b) => b.id === range.id);
                            if (!bracket) return <td key={sched.id} />;

                            if (isEditing) {
                              const editedRates = editState.rates[sched.id];
                              const rateEntry = editedRates?.[rowIdx];
                              return (
                                <td key={sched.id} className="py-1 pr-3">
                                  <input
                                    type="number"
                                    step="0.001"
                                    min="0"
                                    max="1"
                                    value={rateEntry?.rate ?? '0'}
                                    onChange={(e) => {
                                      setEditing((prev) => {
                                        const ps = { ...prev[plan.id]! };
                                        const sr = [...(ps.rates[sched.id] ?? [])];
                                        sr[rowIdx] = { ...sr[rowIdx]!, rate: e.target.value };
                                        return {
                                          ...prev,
                                          [plan.id]: {
                                            ...ps,
                                            rates: { ...ps.rates, [sched.id]: sr },
                                          },
                                        };
                                      });
                                    }}
                                    className="w-20 rounded border border-edge bg-surface px-2 py-0.5 font-mono text-right"
                                  />
                                </td>
                              );
                            }

                            const rateObj = sched.rates.find((r) => r.tierId === range.id);
                            const pct = rateObj
                              ? `${(Number(rateObj.rate) * 100).toFixed(1)}%`
                              : '\u2014';
                            return (
                              <td key={sched.id} className="py-1.5 pr-3 font-mono text-ink">
                                {pct}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-ink-muted">
                  {plan.brackets.length === 0
                    ? 'No brackets configured.'
                    : 'No schedules configured.'}
                </p>
              )}

              {/* Save error */}
              {saveError && (saving === plan.id || saving === null) && (
                <p className="text-xs text-red-600">{saveError}</p>
              )}
            </div>
          );
        })}

        {/* Add Plan inline form */}
        {showAddForm ? (
          <div className="col-span-full rounded-lg border border-dashed border-edge bg-surface-raised p-3 space-y-3">
            <h4 className="text-sm font-semibold text-ink">New Commission Plan</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">Plan Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Recruiter Commission"
                  className="w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">Tier Mode</label>
                <select
                  value={newTierMode}
                  onChange={(e) => setNewTierMode(e.target.value as 'marginal' | 'flat')}
                  className="w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm"
                >
                  <option value="marginal">Marginal</option>
                  <option value="flat">Flat</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">
                  Elevated Period (months)
                </label>
                <input
                  type="number"
                  min="1"
                  value={newElevatedMonths}
                  onChange={(e) => setNewElevatedMonths(e.target.value)}
                  className="w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={creating || !newName.trim()}
                className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create Plan'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setSaveError(null);
                }}
                className="text-xs text-ink-faint hover:text-ink-muted"
              >
                Cancel
              </button>
            </div>
            {saveError && creating && <p className="text-xs text-red-600">{saveError}</p>}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="col-span-full flex items-center gap-1.5 text-xs text-profit hover:underline"
          >
            <span className="text-base leading-none">+</span> Add Plan
          </button>
        )}
      </div>
    </Card>
  );
}

/* CommissionTiersCard removed — functionality consolidated into CommissionPlansCard */

/* ── Commission Allocations Card ─────────────────────────────────────── */

function CommissionAllocationsCard() {
  const { data, error, loading } = useAsyncData(
    () => api.getCommissionAllocations(),
    [],
    'Could not load commission allocations.',
  );
  const [search, setSearch] = useState('');

  if (error)
    return (
      <Card>
        <CardHeader title="Commission Allocations" />
        <p className="px-4 pb-3 text-sm text-red-600">{error}</p>
      </Card>
    );
  if (loading || !data)
    return (
      <Card>
        <CardHeader title="Commission Allocations" />
        <p className="px-4 pb-3 text-sm text-ink-muted">Loading…</p>
      </Card>
    );

  const filtered = search
    ? data.allocations.filter(
        (a) =>
          a.workerName.toLowerCase().includes(search.toLowerCase()) ||
          a.creditedUserName.toLowerCase().includes(search.toLowerCase()) ||
          a.clientName.toLowerCase().includes(search.toLowerCase()),
      )
    : data.allocations;

  return (
    <Card className="flex max-h-[min(36rem,calc(100vh-12rem))] flex-col">
      <CardHeader
        title="Commission Allocations"
        subtitle={`${String(data.allocations.length)} active allocations across ${String(new Set(data.allocations.map((a) => a.creditedUserName)).size)} people`}
        aside={
          <div className="w-48">
            <SearchBar value={search} onChange={setSearch} placeholder="Filter…" />
          </div>
        }
      />
      {filtered.length > 0 ? (
        <Table
          sticky
          head={[
            { label: 'Contractor' },
            { label: 'Customer' },
            { label: 'Recruiter' },
            { label: 'Share', align: 'right' as const },
            { label: 'Since' },
          ]}
        >
          {filtered.map((a) => (
            <Row key={a.id}>
              <Cell>{a.workerName}</Cell>
              <Cell muted>{a.clientName}</Cell>
              <Cell>{a.creditedUserName}</Cell>
              <Cell align="right" mono>
                {(Number(a.share) * 100).toFixed(0)}%
              </Cell>
              <Cell muted>{shortDate(a.effectiveFrom)}</Cell>
            </Row>
          ))}
        </Table>
      ) : (
        <p className="px-4 pb-3 text-sm text-ink-muted">No commission allocations configured.</p>
      )}
    </Card>
  );
}

/* ── Burden Config Card ─────────────────────────────────────────────── */

function BurdenConfigCard() {
  const { data, error, loading, reload } = useAsyncData(
    () => api.getBurdenConfig(),
    [],
    'Could not load burden config.',
  );

  const [editing, setEditing] = useState(false);
  const [basePct, setBasePct] = useState('');
  const [holidayFactor, setHolidayFactor] = useState('');
  const [vacationFactor, setVacationFactor] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const startEdit = () => {
    if (data?.config) {
      setBasePct(data.config.baseBurdenPct);
      setHolidayFactor(data.config.perHolidayDayFactor);
      setVacationFactor(data.config.perVacationDayFactor);
    } else {
      setBasePct('16.5');
      setHolidayFactor('0.5');
      setVacationFactor('0.5');
    }
    setEditing(true);
    setSaveError(null);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.saveBurdenConfig({
        baseBurdenPct: basePct,
        perHolidayDayFactor: holidayFactor,
        perVacationDayFactor: vacationFactor,
      });
      setEditing(false);
      reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Preview calculation: example with 13 holidays + 10 vacation
  const previewPct =
    Number(basePct || 0) + 13 * Number(holidayFactor || 0) + 10 * Number(vacationFactor || 0);
  const previewFactor = (1 + previewPct / 100).toFixed(4);

  if (error)
    return (
      <Card>
        <CardHeader title="Burden / Load Factor Config" />
        <p className="px-4 pb-3 text-sm text-red-600">{error}</p>
      </Card>
    );

  if (loading || !data)
    return (
      <Card>
        <CardHeader title="Burden / Load Factor Config" />
        <p className="px-4 pb-3 text-sm text-ink-muted">Loading…</p>
      </Card>
    );

  const cfg = data.config;

  return (
    <Card>
      <CardHeader
        title="Burden / Load Factor Config"
        subtitle={
          cfg
            ? `Base: ${cfg.baseBurdenPct}%, Holiday: ${cfg.perHolidayDayFactor}/day, Vacation: ${cfg.perVacationDayFactor}/day — effective ${cfg.effectiveFrom}`
            : 'Not configured'
        }
        aside={
          !editing ? (
            <button
              type="button"
              className="rounded-lg border border-profit bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors"
              onClick={startEdit}
            >
              {cfg ? 'Edit' : 'Configure'}
            </button>
          ) : undefined
        }
      />

      {editing && (
        <div className="space-y-3 px-4 pb-4">
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
          <div className="grid grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-ink-muted">Base Burden %</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="mt-1 block w-full rounded border border-border-default bg-surface-default px-3 py-1.5 text-sm"
                value={basePct}
                onChange={(e) => setBasePct(e.target.value)}
              />
              <span className="text-[10px] text-ink-faint">FICA / FUTA / SUTA</span>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-ink-muted">Per Holiday Day Factor</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="mt-1 block w-full rounded border border-border-default bg-surface-default px-3 py-1.5 text-sm"
                value={holidayFactor}
                onChange={(e) => setHolidayFactor(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-ink-muted">Per Vacation Day Factor</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="mt-1 block w-full rounded border border-border-default bg-surface-default px-3 py-1.5 text-sm"
                value={vacationFactor}
                onChange={(e) => setVacationFactor(e.target.value)}
              />
            </label>
          </div>

          <p className="text-xs text-ink-muted">
            Preview (13 holidays + 10 vacation): {basePct}% + 13 × {holidayFactor} + 10 ×{' '}
            {vacationFactor} = {previewPct.toFixed(1)}% → load factor {previewFactor}
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-lg border border-profit bg-profit/10 px-4 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors disabled:opacity-50"
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="rounded border border-border-default px-4 py-1.5 text-xs font-medium text-ink-muted hover:bg-surface-alt"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
